// Settings: one sidebar row, one page of tabs (Users, Shopify, Email, Wise, Claude access). Each tab keeps its own
// address (/settings/users…), so links and ?open= keep working; a tab shows only for the roles its nav.ts entry names.
import { type ComponentType, useEffect } from "react";
import { canSee } from "../lib/access.ts";
import { Link, navigate, usePath } from "../lib/router.tsx";
import { me } from "../lib/session.ts";
import { SETTINGS_TABS } from "../nav.ts";
import { cx, EmptyState } from "../ui/controls.tsx";

const tabsFor = () => SETTINGS_TABS.filter((t) => canSee(t, me()?.role));

/** /settings itself: the first tab this person can see. */
export function SettingsIndex() {
  const first = tabsFor()[0];
  useEffect(() => {
    if (first) navigate(first.path, { replace: true });
  }, [first]);
  return first ? null : <EmptyState title="No settings for your role" />;
}

/** A settings page under the tab bar. */
export function withSettingsTabs(Page: ComponentType): ComponentType {
  return function SettingsTab() {
    const path = usePath();
    return (
      <div className="flex h-full flex-col">
        <nav className="flex h-12 shrink-0 items-center gap-1 border-b border-line bg-surface px-4">
          <h1 className="mr-3 text-[15px] font-semibold">Settings</h1>
          {tabsFor().map((t) => (
            <Link
              key={t.path}
              to={t.path}
              className={cx(
                "flex h-12 items-center border-b-2 px-2.5 text-[13px]",
                path === t.path ? "border-accent font-medium text-text" : "border-transparent text-muted hover:text-text",
              )}
            >
              {t.label}
            </Link>
          ))}
        </nav>
        <div className="min-h-0 flex-1">
          <Page />
        </div>
      </div>
    );
  };
}
